import React, { useState } from 'react';
import { getSections } from './content';
import { cloneDeep } from 'lodash';
import { toast } from 'react-toastify';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { useLazyQuery, useMutation } from '@apollo/client';
import { getZonesByCountry, modifyPerson } from './queries';
import { lookupListAsRecordObject } from '@utils/graph-utils';
import { useParams } from 'react-router-dom';
import { IPersonDetailsDrawerProps } from '.';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { EnhancedButtonStatus } from '@components/_old/EnhancedButton';
import DateService from '@services/DateService';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';

const PersonDetailsDrawer: React.FC<IPersonDetailsDrawerProps> = ({
  section,
  open,
  data,
  lovs,
  onSuccess,
  onClose,
}) => {
  const params = useParams();
  const [formDisabled, setFormDisabled] = useState(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();
  const [localLovs, setLocalLovs] = useState(lovs);

  const [inputsForm, setInputsForm] = useState<IDynamicFormWidgetSection[]>(
    getSections(data, localLovs, section)
  );

  const [personAction] = useMutation(modifyPerson());
  const [getZonesByCountryLazy] = useLazyQuery(getZonesByCountry(), {
    fetchPolicy: 'no-cache',
  });

  const loadZoneLov = async (countryId: string) => {
    const result = await getZonesByCountryLazy({
      variables: { countryID: countryId },
    });
    const zonesData = result.data.Insurance.lookups.zones;
    return lookupListAsRecordObject(zonesData);
  };

  const submitForm = async (data: Record<string, string>) => {
    setFormDisabled(true);
    setSubmitButtonState('loading');

    try {
      const variables = {
        entityId: params.id,
        personDetailsInputs: [
          {
            dateOfBirth: DateService.formatDateBackend(data.dateOfBirth),
            type: section.toUpperCase(),
            fullName: data.fullName || null,
            phoneType: data.phoneType.toUpperCase() || null,
            phoneNumber: data.phoneNumber || null,
            isTaxable: data.isTaxable ?? false,
            occupationClass: data.occupationClass || null,
            profession: data.profession || null,
            country: data.country,
            zone: data.zone || null,
            city: data.city || null,
            street: data.street || null,
            building: data.building || null,
            addressDetails: data.addressDetails || null,
            addressDetailsArabic: data.addressDetailsArabic || null,
            nationalityOne: data.nationalityOne || null,
            personCode: data.personCode || null,
          },
        ],
      };

      const res = await personAction({
        variables,
        errorPolicy: 'all',
      });

      if (!res.errors) {
        toast.success(
          <ToastSuccessMessage>
            {section === 'payer'
              ? 'Payer details successfully updated'
              : 'Owner details successfully created'}
          </ToastSuccessMessage>
        );
        setTimeout(() => {
          setSubmitButtonState('success');
          setFormDisabled(false);
          onSuccess && onSuccess();
          onClose();
        }, 500);
      } else {
        setSubmitButtonState(undefined);
        setFormDisabled(false);
        toast.error(
          <ToastErrorMessage>{res.errors[0].message}</ToastErrorMessage>
        );
      }
    } catch (error) {
      setSubmitButtonState(undefined);
      setFormDisabled(false);
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  const onChange = async (
    fieldName: string,
    value: any,
    sectionIndex: number,
    values: Record<string, any>
  ) => {
    const updatedLovs = cloneDeep(localLovs);
    if (fieldName === 'country') {
      values.zone = '';
      const zoneData = await loadZoneLov(value);
      updatedLovs.zone = zoneData;
      setLocalLovs(updatedLovs);
    }
    const updatedSections = cloneDeep(inputsForm);
    updatedSections[sectionIndex].inputs[fieldName].value = value;
    setInputsForm(updatedSections);
  };

  return (
    <DynamicFormDrawer
      title={
        section === 'payer' ? 'Modify Payer Details' : 'Modify Owner Details'
      }
      onClose={onClose}
      isOpen={open}
      sections={inputsForm}
      onSubmit={(values) => {
        submitForm(values);
      }}
      buttonText={'Submit'}
      submitButtonState={submitButtonState}
      isSubmitButtonDisabled={!!submitButtonState}
      disableForm={formDisabled}
      onChange={onChange}
    />
  );
};

export default PersonDetailsDrawer;
